// @react/components/Table.tsx
// Auto-generated by corebuilder. DO NOT EDIT.

import React, { forwardRef } from 'react';
import { cn } from '@core/ui';

export interface TableProps extends React.TableHTMLAttributes<HTMLTableElement> {
  /** Striped rows */
  striped?: boolean;
  /** Hoverable rows */
  hoverable?: boolean;
  /** Compact padding */
  compact?: boolean;
}

/**
 * Table component with consistent styling.
 *
 * @example
 * ```tsx
 * <Table striped hoverable>
 *   <TableHeader>
 *     <TableRow>
 *       <TableHead>Name</TableHead>
 *       <TableHead>Email</TableHead>
 *       <TableHead>Status</TableHead>
 *     </TableRow>
 *   </TableHeader>
 *   <TableBody>
 *     {users.map(user => (
 *       <TableRow key={user.id}>
 *         <TableCell>{user.name}</TableCell>
 *         <TableCell>{user.email}</TableCell>
 *         <TableCell>
 *           <Badge variant={user.active ? 'success' : 'secondary'}>
 *             {user.active ? 'Active' : 'Inactive'}
 *           </Badge>
 *         </TableCell>
 *       </TableRow>
 *     ))}
 *   </TableBody>
 * </Table>
 * ```
 */
export const Table = forwardRef<HTMLTableElement, TableProps>(
  ({ className, striped = false, hoverable = false, compact = false, ...props }, ref) => {
    return (
      <div className="table-container">
        <table
          ref={ref}
          className={cn(
            'table',
            striped && 'table-striped',
            hoverable && 'table-hoverable',
            compact && 'table-compact',
            className
          )}
          {...props}
        />
      </div>
    );
  }
);

Table.displayName = 'Table';

// Table Header
export interface TableHeaderProps extends React.HTMLAttributes<HTMLTableSectionElement> {}

export const TableHeader = forwardRef<HTMLTableSectionElement, TableHeaderProps>(
  ({ className, ...props }, ref) => {
    return <thead ref={ref} className={cn('table-header', className)} {...props} />;
  }
);

TableHeader.displayName = 'TableHeader';

// Table Body
export interface TableBodyProps extends React.HTMLAttributes<HTMLTableSectionElement> {}

export const TableBody = forwardRef<HTMLTableSectionElement, TableBodyProps>(
  ({ className, ...props }, ref) => {
    return <tbody ref={ref} className={cn('table-body', className)} {...props} />;
  }
);

TableBody.displayName = 'TableBody';

// Table Footer
export interface TableFooterProps extends React.HTMLAttributes<HTMLTableSectionElement> {}

export const TableFooter = forwardRef<HTMLTableSectionElement, TableFooterProps>(
  ({ className, ...props }, ref) => {
    return <tfoot ref={ref} className={cn('table-footer', className)} {...props} />;
  }
);

TableFooter.displayName = 'TableFooter';

// Table Row
export interface TableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  selected?: boolean;
}

export const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, selected = false, ...props }, ref) => {
    return (
      <tr
        ref={ref}
        className={cn('table-row', selected && 'table-row-selected', className)}
        {...props}
      />
    );
  }
);

TableRow.displayName = 'TableRow';

// Table Head Cell
export interface TableHeadProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  sortable?: boolean;
  sortDirection?: 'asc' | 'desc' | null;
}

export const TableHead = forwardRef<HTMLTableCellElement, TableHeadProps>(
  ({ className, sortable = false, sortDirection, children, ...props }, ref) => {
    return (
      <th
        ref={ref}
        className={cn('table-head', sortable && 'table-head-sortable', className)}
        aria-sort={sortDirection === 'asc' ? 'ascending' : sortDirection === 'desc' ? 'descending' : undefined}
        {...props}
      >
        <span className="table-head-content">
          {children}
          {sortable && (
            <span className="table-sort-icon">
              {sortDirection === 'asc' && '↑'}
              {sortDirection === 'desc' && '↓'}
              {!sortDirection && '↕'}
            </span>
          )}
        </span>
      </th>
    );
  }
);

TableHead.displayName = 'TableHead';

// Table Cell
export interface TableCellProps extends React.TdHTMLAttributes<HTMLTableCellElement> {}

export const TableCell = forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ className, ...props }, ref) => {
    return <td ref={ref} className={cn('table-cell', className)} {...props} />;
  }
);

TableCell.displayName = 'TableCell';

// Empty State
export interface TableEmptyProps extends React.HTMLAttributes<HTMLTableRowElement> {
  colSpan: number;
  message?: string;
}

export const TableEmpty = forwardRef<HTMLTableRowElement, TableEmptyProps>(
  ({ className, colSpan, message = 'No data available', children, ...props }, ref) => {
    return (
      <tr ref={ref} className={cn('table-empty-row', className)} {...props}>
        <td colSpan={colSpan} className="table-empty-cell">
          {children || <span className="table-empty-message">{message}</span>}
        </td>
      </tr>
    );
  }
);

TableEmpty.displayName = 'TableEmpty';
